// Raw tool call diagnostics — an internal working-team mode, not a product
// surface. By default the chat shows only the human-readable action; raw tool
// names and argument JSON appear only when this mode is on.
//
// The flag lives in localStorage rather than page state: the chat is mounted in
// three places (chat page, deal card sidebar, contact card) and the mode must be
// the same everywhere. It is not sent to the server — it is a debugging tool,
// not a workspace property.
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'govard.chat.tool-debug';
const memoryStorage: Record<string, string> = {};

const listeners = new Set<() => void>();

function readStorage(): boolean {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const val = window.localStorage.getItem(STORAGE_KEY);
      if (val !== null) return val === 'on';
    }
  } catch {
    // fallback
  }
  return memoryStorage[STORAGE_KEY] === 'on';
}

/** Whether raw tool call details are shown. Off by default. */
export function isToolCallDebugEnabled(): boolean {
  return readStorage();
}

export function setToolCallDebugEnabled(enabled: boolean): void {
  const value = enabled ? 'on' : 'off';
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // fallback
  }
  memoryStorage[STORAGE_KEY] = value;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab may flip the same flag; the storage event does not fire in the
  // tab that wrote it, so the local listener list is still required.
  const onStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function useToolCallDebug(): {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
} {
  const enabled = useSyncExternalStore(subscribe, readStorage, () => false);
  const setEnabled = useCallback((next: boolean) => setToolCallDebugEnabled(next), []);
  return { enabled, setEnabled };
}
